import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchCollaboratorPayoutsOverview,
  fetchAllCollaboratorPayouts,
} from "@/api/collaborator";
import { useSessionState } from "@/hooks/useSessionState";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import {
  CreditCard,
  Search,
  Download,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  Send,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePagination } from "@/hooks/usePagination";
import { updatePayoutStatus } from "@/api/collaborator";
import PayoutActionDialog from "@/components/PayoutActionDialog";

interface CollaboratorPayout {
  id: number;
  collaborator_id: number;
  amount: number;
  status: "pending" | "completed" | "failed";
  payout_date: string | null;
  receipt_url: string | null;
  notes: string | null;
  created_at: string;
  collaborator: {
    id: number;
    name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
  earnings_count: number;
}

const CollaboratorPayoutsList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [payouts, setPayouts] = useState<CollaboratorPayout[]>([]);
  const [filteredPayouts, setFilteredPayouts] = useState<CollaboratorPayout[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useSessionState("collaboratorPayoutsList.searchTerm", "");
  const [statusFilter, setStatusFilter] = useSessionState("collaboratorPayoutsList.statusFilter", "all");
  const [collaboratorFilter, setCollaboratorFilter] = useSessionState("collaboratorPayoutsList.collaboratorFilter", "all");
  const [isUpdatingPayout, setIsUpdatingPayout] = useState<number | null>(null);
  const [isDeletingPayout, setIsDeletingPayout] = useState<number | null>(null);
  const [collaborators, setCollaborators] = useState<
    Array<{ id: number; name: string | null }>
  >([]);

  // Pagination
  const [currentPage, setCurrentPage] = useSessionState("collaboratorPayoutsList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("collaboratorPayoutsList.pageSize", 10);

  // Fetch payouts overview (for cards)
  const { data: payoutsOverview } = useQuery({
    queryKey: ["collaboratorPayoutsOverview"],
    queryFn: fetchCollaboratorPayoutsOverview,
  });

  // Fetch all payouts data
  const { data: allPayoutsData, isLoading: isLoadingPayouts } = useQuery({
    queryKey: ["allCollaboratorPayouts"],
    queryFn: fetchAllCollaboratorPayouts,
  });

  // Process payouts data when API data changes
  useEffect(() => {
    if (!allPayoutsData || isLoadingPayouts) {
      setIsLoading(isLoadingPayouts);
      return;
    }

    const processPayoutsData = async () => {
      setIsLoading(true);
      try {
        if (allPayoutsData.length === 0) {
          setPayouts([]);
          setFilteredPayouts([]);
          setCollaborators([]);
          setIsLoading(false);
          return;
        }

        // Get unique collaborator IDs
        const collaboratorIds = [
          ...new Set(allPayoutsData.map((p) => p.collaborator_id)),
        ];

        // Fetch collaborator profiles
        const { data: collaboratorProfiles, error: collaboratorError } =
          await supabase
            .from("profiles")
            .select("id, name, email, avatar_url")
            .in("id", collaboratorIds);

        if (collaboratorError) throw collaboratorError;

        // Get earnings count for each payout
        const payoutIds = allPayoutsData.map((p) => p.id);
        const { data: earningsData, error: earningsError } = await supabase
          .from("collaborator_earnings")
          .select("payout_id")
          .in("payout_id", payoutIds);

        if (earningsError) throw earningsError;

        // Count earnings per payout
        const earningsCount = (earningsData || []).reduce((acc, earning) => {
          if (earning.payout_id) {
            acc[earning.payout_id] = (acc[earning.payout_id] || 0) + 1;
          }
          return acc;
        }, {} as Record<number, number>);

        // Create lookup map for collaborators
        const collaboratorMap = Object.fromEntries(
          (collaboratorProfiles || []).map((c) => [c.id, c])
        );

        // Combine all data
        const enrichedPayouts: CollaboratorPayout[] = allPayoutsData.map(
          (payout) => ({
            ...payout,
            collaborator: collaboratorMap[payout.collaborator_id],
            earnings_count: earningsCount[payout.id] || 0,
          })
        );

        setPayouts(enrichedPayouts);
        setFilteredPayouts(enrichedPayouts);

        // Set collaborators for filter
        setCollaborators(
          (collaboratorProfiles || []).map((c) => ({ id: c.id, name: c.name }))
        );
      } catch (error) {
        console.error("Error processing payouts data:", error);
        toast({
          title: "Error",
          description: "Failed to process payouts data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    processPayoutsData();
  }, [allPayoutsData, isLoadingPayouts, toast]);

  // Skip page reset on first render (preserves restored page from sessionStorage)
  const isFirstRender = useRef(true);

  // Apply filters
  useEffect(() => {
    let filtered = payouts;

    // Search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (payout) =>
          payout.collaborator?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payout.collaborator?.email
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payout.notes?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((payout) => payout.status === statusFilter);
    }

    // Collaborator filter
    if (collaboratorFilter !== "all") {
      filtered = filtered.filter(
        (payout) => payout.collaborator_id.toString() === collaboratorFilter
      );
    }

    setFilteredPayouts(filtered);
    // Reset to first page when filters change (skip on first render)
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      setCurrentPage(1);
    }
  }, [searchTerm, statusFilter, collaboratorFilter, payouts]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return `₹${amount.toFixed(2)}`;
  };

  // Handle updating payout status
  const handleUpdatePayoutStatus = async (
    payoutId: number,
    status: "completed" | "failed",
    notes?: string,
    receiptUrl?: string
  ) => {
    setIsUpdatingPayout(payoutId);
    try {
      await updatePayoutStatus(payoutId, status, notes, receiptUrl);

      toast({
        title: "Payout Updated",
        description: `Payout has been marked as ${status}.`,
      });

      // Refresh data - queries will automatically refetch
    } catch (error) {
      console.error("Error updating payout status:", error);
      toast({
        title: "Error",
        description: "Failed to update payout status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingPayout(null);
    }
  };

  // Handle deleting payout
  const handleDeletePayout = async (payoutId: number) => {
    setIsDeletingPayout(payoutId);
    try {
      const { error } = await supabase
        .from("collaborator_payouts")
        .delete()
        .eq("id", payoutId);

      if (error) throw error;

      toast({
        title: "Payout Deleted",
        description:
          "Payout has been deleted and associated earnings are now available for new payout requests.",
      });

      // Refresh data - queries will automatically refetch
    } catch (error) {
      console.error("Error deleting payout:", error);
      toast({
        title: "Error",
        description: "Failed to delete payout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingPayout(null);
    }
  };

  // Get status badge props
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return {
          variant: "default" as const,
          className: "bg-green-500",
          icon: <CheckCircle className="h-3 w-3 mr-1" />,
        };
      case "pending":
        return {
          variant: "default" as const,
          className: "bg-amber-500",
          icon: <Clock className="h-3 w-3 mr-1" />,
        };
      case "failed":
        return {
          variant: "destructive" as const,
          className: "",
          icon: <XCircle className="h-3 w-3 mr-1" />,
        };
      default:
        return {
          variant: "outline" as const,
          className: "",
          icon: null,
        };
    }
  };

  // Pagination
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: filteredPayouts.length,
    itemsPerPage: pageSize,
    currentPage,
  });

  const paginatedPayouts = filteredPayouts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (isLoading) {
    return (
      <AdminLayout title="Collaborator Payouts">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Collaborator Payouts">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-2">
          <CreditCard className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Collaborator Payouts</h1>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Payouts
                  </p>
                  <p className="text-2xl font-bold">
                    {payoutsOverview?.totalPayouts || 0}
                  </p>
                </div>
                <Users className="h-5 w-5 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Pending</p>
                  <p className="text-2xl font-bold">
                    {payoutsOverview?.pendingPayouts || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(payoutsOverview?.pendingAmount || 0)}
                  </p>
                </div>
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Completed
                  </p>
                  <p className="text-2xl font-bold">
                    {payoutsOverview?.completedPayouts || 0}
                  </p>
                </div>
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Total Amount
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(payoutsOverview?.totalAmount || 0)}
                  </p>
                </div>
                <DollarSign className="h-5 w-5 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search payouts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={collaboratorFilter}
            onValueChange={setCollaboratorFilter}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by collaborator" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Collaborators</SelectItem>
              {collaborators.map((collaborator) => (
                <SelectItem
                  key={collaborator.id}
                  value={collaborator.id.toString()}
                >
                  {collaborator.name || `User ${collaborator.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon">
            <Download className="h-4 w-4" />
          </Button>
        </div>

        {/* Payouts Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Payouts</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPayouts.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Payouts Found</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {payouts.length === 0
                    ? "No collaborator payouts have been created yet."
                    : "No payouts match your current filters."}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Created</TableHead>
                        <TableHead>Collaborator</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Earnings</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payout Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedPayouts.map((payout) => {
                        const { variant, className, icon } = getStatusBadge(
                          payout.status
                        );
                        return (
                          <TableRow key={payout.id}>
                            <TableCell>
                              <span className="text-sm">
                                {format(
                                  new Date(payout.created_at),
                                  "MMM dd, yyyy HH:mm"
                                )}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="w-6 h-6">
                                  <AvatarImage
                                    src={
                                      payout.collaborator?.avatar_url ||
                                      undefined
                                    }
                                  />
                                  <AvatarFallback className="text-xs">
                                    {payout.collaborator?.name?.charAt(0) ||
                                      "U"}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="text-sm font-medium">
                                    {payout.collaborator?.name || "Unknown"}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {payout.collaborator?.email}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(payout.amount)}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {payout.earnings_count} earnings
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant={variant} className={className}>
                                {icon}
                                {payout.status.charAt(0).toUpperCase() +
                                  payout.status.slice(1)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {payout.payout_date ? (
                                <span className="text-sm">
                                  {format(
                                    new Date(payout.payout_date),
                                    "MMM dd, yyyy HH:mm"
                                  )}
                                </span>
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  Not paid yet
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/admin/collaborator/${payout.collaborator_id}`
                                    )
                                  }
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  View
                                </Button>

                                {payout.status === "pending" && (
                                  <>
                                    <PayoutActionDialog
                                      payout={payout}
                                      action="complete"
                                      onAction={handleUpdatePayoutStatus}
                                      formatCurrency={formatCurrency}
                                      isLoading={isUpdatingPayout === payout.id}
                                    />
                                    <PayoutActionDialog
                                      payout={payout}
                                      action="fail"
                                      onAction={handleUpdatePayoutStatus}
                                      formatCurrency={formatCurrency}
                                      isLoading={isUpdatingPayout === payout.id}
                                    />

                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          disabled={
                                            isDeletingPayout === payout.id
                                          }
                                        >
                                          {isDeletingPayout === payout.id ? (
                                            <Clock className="h-3 w-3 animate-spin" />
                                          ) : (
                                            <Trash2 className="h-3 w-3" />
                                          )}
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>
                                            Delete Payout
                                          </AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Are you sure you want to delete this
                                            payout? This will make the
                                            associated earnings available for
                                            new payout requests. This action
                                            cannot be undone.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>
                                            Cancel
                                          </AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() =>
                                              handleDeletePayout(payout.id)
                                            }
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                          >
                                            Delete
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination Controls */}
                {filteredPayouts.length > 0 && (
                  <div className="flex items-center justify-between px-4 py-4 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      Showing{" "}
                      <span className="font-medium">
                        {filteredPayouts.length === 0
                          ? 0
                          : (currentPage - 1) * pageSize + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(
                          currentPage * pageSize,
                          filteredPayouts.length
                        )}
                      </span>{" "}
                      of{" "}
                      <span className="font-medium">
                        {filteredPayouts.length}
                      </span>{" "}
                      payouts
                    </div>
                    <div className="flex items-center gap-4">
                      {totalPages > 0 && (
                        <div className="flex items-center">
                          <Button
                            className="mr-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(1)}
                            disabled={!canGoPrev}
                          >
                            First
                          </Button>

                          <Button
                            className="mr-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCurrentPage(
                                currentPage > 1 ? currentPage - 1 : 1
                              )
                            }
                            disabled={!canGoPrev}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>

                          {pageNumbers.map((pageNum) => (
                            <Button
                              key={pageNum}
                              className="mx-0.5 w-8 h-8 p-0"
                              variant={
                                pageNum === currentPage ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          ))}

                          <Button
                            className="ml-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCurrentPage(
                                currentPage < totalPages
                                  ? currentPage + 1
                                  : totalPages
                              )
                            }
                            disabled={!canGoNext}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>

                          <Button
                            className="ml-1 px-2"
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={!canGoNext}
                          >
                            Last
                          </Button>
                        </div>
                      )}

                      <select
                        className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1); // Reset to first page when changing page size
                        }}
                      >
                        {[10, 20, 30, 50].map((size) => (
                          <option key={size} value={size}>
                            {size} per page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default CollaboratorPayoutsList;
